import React, { useState, useEffect } from "react";
import { CheckIcon } from "@heroicons/react/solid";
import cclogo from "./../../../assets/cc/cc.xlarge.png";
import bylogo from "./../../../assets/cc/by.xlarge.png";
import nceulogo from "./../../../assets/cc/nc-eu.xlarge.png";
import salogo from "./../../../assets/cc/sa.xlarge.png";
import ndlogo from "./../../../assets/cc/nd.xlarge.png";
import EasyTrans from "../../bundel/EasyTrans";

/** Selectable card used for the CC-Kit yes/no questions. */
function ChoiceCard({ selected, onSelect, children }) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`flex flex-1 items-center justify-between gap-2 rounded-xl border p-3 text-left text-sm transition focus:outline-none focus:ring-2 focus:ring-fairblue-500 ${
        selected
          ? "border-fairblue-500 bg-fairblue-50 font-medium text-fairblue-700 ring-1 ring-fairblue-500"
          : "border-gray-200 bg-white text-gray-700 hover:border-gray-300"
      }`}
    >
      <span>{children}</span>
      <span
        className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border-2 ${
          selected
            ? "border-fairblue-500 bg-fairblue-500 text-white"
            : "border-gray-300"
        }`}
      >
        {selected && <CheckIcon className="h-3 w-3" aria-hidden="true" />}
      </span>
    </button>
  );
}

const CC_BY_4_0 = "CC-BY-4.0";
const Attribution = "Attribution (CC BY 4.0)";

const CC_BY_NC_4_0 = "CC-BY-NC-4.0";
const AttributionNonCommercial = "Attribution-NonCommercial (CC BY-NC)";

const CC_BY_ND_4_0 = "CC-BY-ND-4.0";
const AttributionNoDerivatives = "Attribution-NoDerivatives (CC BY-ND)";

const CC_BY_NC_ND_4_0 = "CC-BY-NC-ND-4.0";
const AttributionNonCommercialNoDerivatives =
  "Attribution-NonCommercial-NoDerivatives (CC BY-NC-ND)";

const CC_BY_SA_4_0 = "CC-BY-SA-4.0";
const AttributionShareAlike = "Attribution-ShareAlike(CC BY-SA)";

const CC_BY_NC_SA_4_0 = "CC-BY-NC-SA-4.0";
const AttributionNonCommercialShareAlike =
  "Attribution-NonCommercial-ShareAlike (CC BY-NC-SA)";

const CCKitHelper = (props) => {
  const [shareAllowed, setShareAllowed] = useState(true);
  const [commercialAllowed, setCommercialAllowed] = useState(true);
  const [withSameCondition, setWithSameCondition] = useState(false);
  const [selectedLicenceCode, setSelectedLicenceCode] = useState("");
  const [selectedLicence, setSelectedLicence] = useState(
    "Attribution (CC BY 4.0)",
  );

  useEffect(() => {
    // selectLicence() already publishes the SPDX code via onSetLicence; do not
    // overwrite it here with the human-readable label (that label used to get
    // persisted as the work's license identifier and broke license filtering).
    selectLicence(true, true, false);
  }, []);

  function selectLicence(
    shareAllowedParameter,
    commercialAllowedParameter,
    withSameConditionParameter,
  ) {
    let selectedLicenceCode = "";
    let selectedLicence = "";

    switch (true) {
      case shareAllowedParameter &&
        commercialAllowedParameter &&
        !withSameConditionParameter:
        selectedLicenceCode = CC_BY_4_0;
        selectedLicence = Attribution;
        break;
      case shareAllowedParameter &&
        !commercialAllowedParameter &&
        !withSameConditionParameter:
        selectedLicenceCode = CC_BY_NC_4_0;
        selectedLicence = AttributionNonCommercial;
        break;
      case !shareAllowedParameter &&
        commercialAllowedParameter &&
        !withSameConditionParameter:
        selectedLicenceCode = CC_BY_ND_4_0;
        selectedLicence = AttributionNoDerivatives;
        break;
      case !shareAllowedParameter &&
        !commercialAllowedParameter &&
        !withSameConditionParameter:
        selectedLicenceCode = CC_BY_NC_ND_4_0;
        selectedLicence = AttributionNonCommercialNoDerivatives;
        break;
      case withSameConditionParameter && commercialAllowedParameter:
        selectedLicenceCode = CC_BY_SA_4_0;
        selectedLicence = AttributionShareAlike;
        break;
      case withSameConditionParameter && !commercialAllowedParameter:
        selectedLicenceCode = CC_BY_NC_SA_4_0;
        selectedLicence = AttributionNonCommercialShareAlike;
        break;
      default:
        selectedLicenceCode = "";
        selectedLicence = "";
    }

    setSelectedLicenceCode(selectedLicenceCode);
    setSelectedLicence(selectedLicence);
    props.onSetLicence(selectedLicenceCode);
  }

  // derive the active sharing choice from the two underlying flags
  const sharingChoice = withSameCondition
    ? "sa"
    : shareAllowed
      ? "yes"
      : "nd";

  const chooseSharing = (choice) => {
    if (choice === "yes") {
      setShareAllowed(true);
      setWithSameCondition(false);
      selectLicence(true, commercialAllowed, false);
    } else if (choice === "nd") {
      setShareAllowed(false);
      setWithSameCondition(false);
      selectLicence(false, commercialAllowed, false);
    } else {
      setShareAllowed(true);
      setWithSameCondition(true);
      selectLicence(true, commercialAllowed, true);
    }
  };

  const chooseCommercial = (allowed) => {
    setCommercialAllowed(allowed);
    selectLicence(shareAllowed, allowed, withSameCondition);
  };

  return (
    <div className="space-y-6">
      <div>
        <legend className="text-base font-medium text-gray-900">
          <EasyTrans>RegisterWork.License.LicenseKit.CCLicense</EasyTrans>
        </legend>
        <p className="mt-1 text-sm text-gray-500">
          <EasyTrans>RegisterWork.License.LicenseKit.Description</EasyTrans>
        </p>
      </div>

      <div>
        <p className="mb-2 text-sm font-medium text-gray-900">
          <EasyTrans>RegisterWork.License.LicenseKit.AllowSharedEdits</EasyTrans>
        </p>
        <div className="flex flex-col gap-2 sm:flex-row">
          <ChoiceCard
            selected={sharingChoice === "yes"}
            onSelect={() => chooseSharing("yes")}
          >
            <EasyTrans>RegisterWork.License.LicenseKit.Yes</EasyTrans>
          </ChoiceCard>
          <ChoiceCard
            selected={sharingChoice === "sa"}
            onSelect={() => chooseSharing("sa")}
          >
            <EasyTrans>
              RegisterWork.License.LicenseKit.AllowSharedSameConditions
            </EasyTrans>
          </ChoiceCard>
          <ChoiceCard
            selected={sharingChoice === "nd"}
            onSelect={() => chooseSharing("nd")}
          >
            <EasyTrans>RegisterWork.License.LicenseKit.No</EasyTrans>
          </ChoiceCard>
        </div>
      </div>

      <div>
        <p className="mb-2 text-sm font-medium text-gray-900">
          <EasyTrans>RegisterWork.License.LicenseKit.AllowCommercialUse</EasyTrans>
        </p>
        <div className="flex flex-col gap-2 sm:flex-row">
          <ChoiceCard
            selected={commercialAllowed}
            onSelect={() => chooseCommercial(true)}
          >
            <EasyTrans>RegisterWork.License.LicenseKit.Yes</EasyTrans>
          </ChoiceCard>
          <ChoiceCard
            selected={!commercialAllowed}
            onSelect={() => chooseCommercial(false)}
          >
            <EasyTrans>RegisterWork.License.LicenseKit.No</EasyTrans>
          </ChoiceCard>
        </div>
      </div>

      {/* live license preview */}
      <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
        <div className="flex flex-row items-center justify-center gap-1">
          <img className="h-14" src={cclogo} alt="cclogo" />
          <img className="h-14" src={bylogo} alt="bylogo" />
          {!commercialAllowed && (
            <img className="h-14" src={nceulogo} alt="nceulogo" />
          )}
          {!shareAllowed && <img className="h-14" src={ndlogo} alt="ndlogo" />}
          {withSameCondition && (
            <img className="h-14" src={salogo} alt="salogo" />
          )}
        </div>
        <div className="mt-3 text-center">
          <p className="text-sm font-semibold text-gray-900">
            {selectedLicence}
          </p>
          {selectedLicenceCode && (
            <p className="mt-0.5 text-xs text-gray-500">{selectedLicenceCode}</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default CCKitHelper;
